/**
 * Canais de chat (A144, 11/10/2026): a marca escolhe quais chats quer ter —
 * "Administrativo", "Clínico", ou o nome que quiser — e quem da equipe atende
 * em cada um. A paciente conversa com o CANAL, não com uma pessoa: vê o nome
 * do canal, e (se o canal quiser) o primeiro nome de quem respondeu. Quem
 * atende o canal vê as conversas dele numa caixa compartilhada.
 *
 * Nasce da reunião de 22/09/2026 com a Nati: a fricção do chat era aparecer o
 * nome das pessoas, e a política de pares (A030) — 6 papéis, 36 combinações,
 * 14 controles — ficou complexa demais de configurar. Os canais substituem a
 * matriz para o que a paciente vê; a matriz sai numa fase seguinte.
 *
 * O mantenedor cria os canais iniciais no assistente de criação de marca; a
 * marca ajusta, cria e apaga os seus. Apagar é `deletedAt`.
 */
import { z } from 'zod';
import { brandIdField } from './brand';

export const NOME_DO_CANAL_MAX = 40;
export const DESCRICAO_DO_CANAL_MAX = 120;
/** A conversa aceita até 25 participantes: a paciente e até 24 de quem atende. */
export const MEMBROS_DO_CANAL_MAX = 24;

export const IconeDoCanalEnum = z.enum(['CONVERSA', 'AJUDA', 'ESTETOSCOPIO', 'CORACAO', 'PRANCHETA', 'BEBE', 'CALENDARIO', 'ESTRELA']);
export type IconeDoCanal = z.infer<typeof IconeDoCanalEnum>;

/** Para quem o canal aparece: as pacientes, ou as gestoras das empresas clientes. */
export const PublicoDoCanalEnum = z.enum(['PACIENTES', 'GESTORAS']);
export type PublicoDoCanal = z.infer<typeof PublicoDoCanalEnum>;

export const CanalDeChatSchema = z.object({
  id: z.string(),
  brandId: brandIdField(),
  nome: z.string().trim().min(1).max(NOME_DO_CANAL_MAX),
  descricao: z.string().trim().max(DESCRICAO_DO_CANAL_MAX).nullable().optional(),
  icone: IconeDoCanalEnum.default('CONVERSA'),
  /**
   * A foto do canal (A149): o rosto da profissional que atende, a logo da
   * marca no suporte. Opcional — sem ela, o ícone. Pública, como o logo.
   */
  fotoUrl: z.string().url().nullable().optional(),
  publico: PublicoDoCanalEnum.default('PACIENTES'),
  /** Quem atende: ids de conta da equipe da marca (ou da rede recebida por concessão). */
  membros: z.array(z.string()).max(MEMBROS_DO_CANAL_MAX).default([]),
  /** Nas mensagens, a paciente vê o primeiro nome de quem respondeu ("Mayra · Clínico"). Desligado: só o canal. */
  mostrarNomeDeQuemResponde: z.boolean().default(true),
  /** Só quem aderiu ao programa da marca (a barreira de adesão) conversa com este canal. */
  soParaQuemAderiu: z.boolean().default(false),
  ativo: z.boolean().default(true),
  ordem: z.number().int().default(0),
  criadoPor: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().default(null),
});
export type CanalDeChat = z.infer<typeof CanalDeChatSchema>;

/** O que a marca (ou o mantenedor, na criação) envia. */
export const DadosDoCanalDeChatSchema = CanalDeChatSchema.pick({
  nome: true,
  descricao: true,
  icone: true,
  fotoUrl: true,
  publico: true,
  membros: true,
  mostrarNomeDeQuemResponde: true,
  soParaQuemAderiu: true,
  ativo: true,
  ordem: true,
});
export type DadosDoCanalDeChat = z.infer<typeof DadosDoCanalDeChatSchema>;

/** Os canais que o assistente de criação de marca sugere (decisão do Fábio, 11/10/2026). */
export const CANAIS_INICIAIS: Array<Pick<DadosDoCanalDeChat, 'nome' | 'descricao' | 'icone'>> = [
  { nome: 'Administrativo', descricao: 'Dúvidas gerais, cadastro e acesso', icone: 'AJUDA' },
  { nome: 'Clínico', descricao: 'Dúvidas de saúde com a equipe', icone: 'ESTETOSCOPIO' },
];
